import React from 'react';
import { Helmet } from 'react-helmet';
import { Type as RibbonType } from 'domain/constants/ribbons';
import { isNotNilNorEmpty, isNilOrEmpty } from 'presentation/utils/generalUtils';
import { Ribbon, Ribbons } from 'core/page';

import { ShortcodeProps } from '../Shortcode';

export interface OuterProps {
  ribbons: Ribbons;
}

export interface MainEntityType {
  '@type': string;
  'name': string;
  'acceptedAnswer': {
    '@type': string;
    'text': string;
  };
}

interface ItemType {
  answer: ShortcodeProps;
  defaultOpen: boolean;
  question: 'Q3';
}

const createAnswer = (answer: ShortcodeProps) => {
  const parsedAnswer = [] as Array<string>;
  let stringedAnswer = '' as string;
  const explodeShortcode = (shortcode: ShortcodeProps) => {
    if (typeof shortcode === 'string') {
      parsedAnswer.push(shortcode);
      return;
    }
    const toParse = shortcode?.elements ?? shortcode;
    toParse.forEach(function (value: ShortcodeProps) {
      if (typeof value === 'string' && value) {
        parsedAnswer.push(value);
      }
      if (value.type === 'text' && value.text) {
        parsedAnswer.push(value.text);
      }
      // @ts-ignore: error after upgrading to ux-configs
      if (value.name === 'tooltip' && value.elements && value.attributes.label) {
        // @ts-ignore: error after upgrading to ux-configs
        parsedAnswer.push(`${value.attributes.label} (`);
      }
      if (value.elements) {
        explodeShortcode(value);
      }
      // @ts-ignore: error after upgrading to ux-configs
      if (value.name === 'tooltip' && value.elements && value.attributes.label) {
        parsedAnswer.push(')');
      }
    });
  };
  explodeShortcode(answer);
  // transform back into array
  stringedAnswer = parsedAnswer.join('');
  // sometimes we start with a newline, we don't want to change it to a comma
  stringedAnswer = stringedAnswer.startsWith('\n') ? stringedAnswer.substring(1) : stringedAnswer;
  // sometimes we end with a newline, we don't want to change it to a comma
  stringedAnswer = stringedAnswer.endsWith('\n')
    ? `${stringedAnswer.slice(0, stringedAnswer.length - 1)}.`
    : stringedAnswer;
  // replace full stops
  stringedAnswer = stringedAnswer.split('\n\n').join('. ');
  // replace start line stops
  stringedAnswer = stringedAnswer.split(' \n').join(' ');
  // replace end line stops
  stringedAnswer = stringedAnswer.split('\n').join(', ');
  // trim any remaining noise
  stringedAnswer = stringedAnswer.trim();
  return stringedAnswer;
};

const createMainEntity = (ribbons: Ribbons) => {
  return ribbons.reduce((mainEntity: Array<MainEntityType>, ribbon: Ribbon) => {
    const items = ribbon.props?.items ?? [];
    const ribbonEntity = items.map((item: ItemType) => ({
      '@type': 'Question',
      'name': item.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        // replace multiple spaces
        'text': createAnswer(item.answer),
      },
    }));

    return mainEntity.concat(ribbonEntity);
  }, []);
};

export const getJsonLd = (ribbons: Ribbons) => {
  const faqRibbons = ribbons.filter((ribbon: Ribbon) => {
    return ribbon.type === RibbonType.ELABORATE && ribbon.props.context === 'FAQ';
  });

  if (isNilOrEmpty(faqRibbons)) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': createMainEntity(faqRibbons),
  };
};

const FaqJsonLd = ({ ribbons }: OuterProps) => {
  const jsonLD = getJsonLd(ribbons);
  const hasJsonLD = isNotNilNorEmpty(jsonLD);

  if (hasJsonLD) {
    return (
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLD)}</script>
      </Helmet>
    );
  }

  return null;
};

export default FaqJsonLd;
